import {
  LayoutDashboard,
  Lightbulb,
  Calendar,
  MessageSquare,
  BookOpen,
  FileCode,
  Users,
  UserCog,
  Home,
  User,
  FileText,
  Activity,
  ClipboardList,
} from 'lucide-react';
import { HomeIcon } from '@/shared/icons/HomeIcon';
import { DevicesIcon } from '@/shared/icons/DevicesIcon';
import { AIAssistantIcon } from '@/shared/icons/AIAssistantIcon';
import { AIIcon } from '@/shared/icons/AIIcon';
import { BellIcon } from '@/shared/icons/BellIcon';
import { DoorOpen } from 'lucide-react';
import type { SidebarConfig, NavGroup } from './types';

// House section for homeowners
const houseGroupHomeowner: NavGroup = {
  id: 'house',
  label: 'House',
  labelKey: 'sidebar.groups.house',
  icon: HomeIcon,
  defaultExpanded: true,
  items: [
    { id: 'dashboard', href: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', labelKey: 'sidebar.items.dashboard' },
    { id: 'devices', href: '/devices', icon: DevicesIcon, label: 'Devices', labelKey: 'sidebar.items.devices' },
    { id: 'network', href: '/network', icon: Activity, label: 'Network', labelKey: 'sidebar.items.network' },
    { id: 'access-control', href: '/access-control', icon: DoorOpen, label: 'Access control', labelKey: 'sidebar.items.accessControl' },
  ],
};

// House section for technicians/service providers
const houseGroupTechnician: NavGroup = {
  id: 'house',
  label: 'House',
  labelKey: 'sidebar.groups.house',
  icon: HomeIcon,
  defaultExpanded: true,
  items: [
    { id: 'dashboard', href: '/service-provider/dashboard', icon: LayoutDashboard, label: 'Dashboard', labelKey: 'sidebar.items.dashboard' },
    { id: 'devices', href: '/service-provider/devices', icon: DevicesIcon, label: 'Devices', labelKey: 'sidebar.items.devices' },
    { id: 'network', href: '/service-provider/network', icon: Activity, label: 'Network', labelKey: 'sidebar.items.network' },
    { id: 'access-control', href: '/service-provider/access-control', icon: DoorOpen, label: 'Access control', labelKey: 'sidebar.items.accessControl' },
  ],
};

// AI section for homeowners
const aiGroupHomeowner: NavGroup = {
  id: 'ai',
  label: 'AI',
  labelKey: 'sidebar.groups.ai',
  icon: AIIcon,
  defaultExpanded: true,
  items: [
    { id: 'ai-assistant', href: '/ai-assistant', icon: AIAssistantIcon, label: 'AI Assistant', labelKey: 'sidebar.items.aiAssistant', beta: true },
    { id: 'optimizations', href: '/optimizations', icon: Lightbulb, label: 'Optimizations', labelKey: 'sidebar.items.optimizations', beta: true },
  ],
};

// AI section for technicians/service providers
const aiGroupTechnician: NavGroup = {
  id: 'ai',
  label: 'AI',
  labelKey: 'sidebar.groups.ai',
  icon: AIIcon,
  defaultExpanded: true,
  items: [
    { id: 'ai-assistant', href: '/service-provider/ai-assistant', icon: AIAssistantIcon, label: 'AI Assistant', labelKey: 'sidebar.items.aiAssistant', beta: true },
    { id: 'optimizations', href: '/service-provider/optimizations', icon: Lightbulb, label: 'Optimizations', labelKey: 'sidebar.items.optimizations', beta: true },
  ],
};

// Management section for homeowners
const managementGroupHomeowner: NavGroup = {
  id: 'management',
  label: 'Management',
  labelKey: 'sidebar.groups.management',
  icon: Calendar,
  defaultExpanded: true,
  items: [
    { id: 'calendar', href: '/calendar', icon: Calendar, label: 'Calendar', labelKey: 'sidebar.items.calendar' },
    { id: 'tasks', href: '/tasks', icon: ClipboardList, label: 'Tasks', labelKey: 'sidebar.items.tasks' },
    { id: 'chat', href: '/chat', icon: MessageSquare, label: 'Chat', labelKey: 'sidebar.items.chat' },
    { id: 'service-providers', href: '/homeowner', icon: Users, label: 'Service providers', labelKey: 'sidebar.items.serviceProviders' },
  ],
};

// Management section for technicians/service providers
const managementGroupTechnician: NavGroup = {
  id: 'management',
  label: 'Management',
  labelKey: 'sidebar.groups.management',
  icon: Calendar,
  defaultExpanded: true,
  items: [
    { id: 'calendar', href: '/service-provider/calendar', icon: Calendar, label: 'Calendar', labelKey: 'sidebar.items.calendar' },
    { id: 'tasks', href: '/tasks', icon: ClipboardList, label: 'Tasks', labelKey: 'sidebar.items.tasks' },
    { id: 'chat', href: '/service-provider/chat', icon: MessageSquare, label: 'Chat', labelKey: 'sidebar.items.chat' },
    { id: 'customers', href: '/service-provider/customers', icon: Users, label: 'Customers', labelKey: 'sidebar.items.customers' },
    { id: 'team', href: '/service-provider/team', icon: UserCog, label: 'Team', labelKey: 'sidebar.items.team' },
  ],
};

// Documentation section for homeowners
const documentationGroupHomeowner: NavGroup = {
  id: 'documentation',
  label: 'Documentation',
  labelKey: 'sidebar.groups.documentation',
  icon: FileText,
  defaultExpanded: true,
  items: [
    { id: 'my-documents', href: '/my-documents', icon: FileCode, label: 'My Documents', labelKey: 'sidebar.items.myDocuments' },
    { id: 'zentri-hub', href: '/zentri-hub', icon: BookOpen, label: 'Zentri-HUB', labelKey: 'sidebar.items.zentriHub', beta: true },
  ],
};

// Documentation section for technicians
const documentationGroupTechnician: NavGroup = {
  id: 'documentation',
  label: 'Documentation',
  labelKey: 'sidebar.groups.documentation',
  icon: FileText,
  defaultExpanded: true,
  items: [
    { id: 'documents', href: '/service-provider/documents', icon: FileCode, label: 'My Documents', labelKey: 'sidebar.items.myDocuments' },
    { id: 'zentri-hub', href: '/service-provider/zentri-hub', icon: BookOpen, label: 'Zentri-HUB', labelKey: 'sidebar.items.zentriHub', beta: true },
  ],
};

// User section for homeowners
const userGroupHomeowner: NavGroup = {
  id: 'user',
  label: 'User',
  labelKey: 'sidebar.groups.user',
  icon: User,
  defaultExpanded: true,
  items: [
    { id: 'profile', href: '/home-owner/profile', icon: UserCog, label: 'Profile', labelKey: 'sidebar.items.profile' },
    // Properties tab temporarily hidden (may come back later):
    // { id: 'properties', href: '/home-owner/property-profiles', icon: Home, label: 'Properties', labelKey: 'sidebar.items.properties' },
    // Members (invite by email + assign properties). Owner-only: a view-only
    // member (investor/Allianz) must not see "invite members" (#2912, #2913).
    { id: 'members', href: '/homeowner/members', icon: Users, label: 'Members', labelKey: 'sidebar.items.members', ownerOnly: true },
    { id: 'alarms', href: '/alarms', icon: BellIcon, label: 'Alarms', labelKey: 'sidebar.items.alarms' },
  ],
};

// User section for technicians/service providers
const userGroupTechnician: NavGroup = {
  id: 'user',
  label: 'User',
  labelKey: 'sidebar.groups.user',
  icon: User,
  defaultExpanded: true,
  items: [
    { id: 'profile', href: '/service-provider/profile', icon: UserCog, label: 'Profile', labelKey: 'sidebar.items.profile' },
    { id: 'properties', href: '/service-provider/property-profiles', icon: Home, label: 'Properties', labelKey: 'sidebar.items.properties' },
    { id: 'alarms', href: '/service-provider/alarms', icon: BellIcon, label: 'Alarms', labelKey: 'sidebar.items.alarms' },
  ],
};

// Homeowner sidebar configuration
export const HOMEOWNER_SIDEBAR_CONFIG: SidebarConfig = {
  showPropertySelector: true,
  showLogo: true,
  navGroups: [
    houseGroupHomeowner,
    aiGroupHomeowner,
    managementGroupHomeowner,
    documentationGroupHomeowner,
    userGroupHomeowner,
  ],
};

// Technician/Service Provider sidebar configuration
export const TECHNICIAN_SIDEBAR_CONFIG: SidebarConfig = {
  showPropertySelector: true,
  showLogo: true,
  navGroups: [
    houseGroupTechnician,
    aiGroupTechnician,
    managementGroupTechnician,
    documentationGroupTechnician,
    userGroupTechnician,
  ],
};

// Admin navigation groups
const adminMainGroup: NavGroup = {
  id: 'main',
  label: 'MAIN',
  labelKey: 'sidebar.groups.main',
  icon: LayoutDashboard,
  defaultExpanded: true,
  items: [
    { id: 'admin-dashboard', href: '/admin', icon: LayoutDashboard, label: 'Dashboard', labelKey: 'sidebar.items.dashboard' },
    { id: 'sp-applications', href: '/admin/sp-applications', icon: UserCog, label: 'Service Providers', labelKey: 'sidebar.items.serviceProviders' },
    { id: 'directory', href: '/admin/directory', icon: Users, label: 'Directory', labelKey: 'sidebar.items.directory' },
    { id: 'vendors', href: '/admin/vendors', icon: Lightbulb, label: 'Vendors', labelKey: 'sidebar.items.vendors' },
    { id: 'hub-documents', href: '/admin/hub-documents', icon: BookOpen, label: 'Hub Documents', labelKey: 'sidebar.items.hubDocuments' },
    { id: 'simulator', href: '/admin/simulator', icon: LayoutDashboard, label: 'Simulator', labelKey: 'sidebar.items.simulator' },
  ],
};

const adminIntegrationsGroup: NavGroup = {
  id: 'integrations',
  label: 'INTEGRATIONS',
  labelKey: 'sidebar.groups.integrations',
  icon: LayoutDashboard,
  defaultExpanded: true,
  items: [
    { id: 'conflicts', href: '/admin/integrations/conflicts', icon: LayoutDashboard, label: 'Conflicts', labelKey: 'sidebar.items.conflicts' },
    { id: 'monitoring', href: '/admin/integrations/monitoring', icon: LayoutDashboard, label: 'Monitoring', labelKey: 'sidebar.items.monitoring' },
  ],
};

const adminToolsGroup: NavGroup = {
  id: 'tools',
  label: 'TOOLS',
  labelKey: 'sidebar.groups.tools',
  icon: LayoutDashboard,
  defaultExpanded: true,
  items: [
    { id: 'crawler', href: '/admin/crawler', icon: LayoutDashboard, label: 'Crawler Control', labelKey: 'sidebar.items.crawlerControl' },
  ],
};

// Admin sidebar configuration
export const ADMIN_SIDEBAR_CONFIG: SidebarConfig = {
  showPropertySelector: false,
  showLogo: true,
  navGroups: [
    adminMainGroup,
    adminIntegrationsGroup,
    adminToolsGroup,
  ],
};

// Helper function to get config based on role
export const getSidebarConfig = (role: 'homeowner' | 'technician' | 'admin'): SidebarConfig => {
  switch (role) {
    case 'technician':
      return TECHNICIAN_SIDEBAR_CONFIG;
    case 'admin':
      return ADMIN_SIDEBAR_CONFIG;
    case 'homeowner':
    default:
      return HOMEOWNER_SIDEBAR_CONFIG;
  }
};
